/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Entity, SSOConfiguration} from '@ion/core/src/types/entities';
import {useAddSSOConfiguration} from '@ion/query/src/hooks/useAddSSOConfiguration';
import {errorAddOptions} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {saveSSOAccount} from './saveSSOAccount';
import SSOEditor from './SSOEditor';
import {addSSOConfig} from './state';
import {addSSOSelectedSelector} from './state-selectors';

interface AddSSOEditorProps {
  refreshInitialization?: Function;
};

/**
 * Dialog component for a user to add a SSO Configuration.
 *
 * ```typescript
 *
 *  import AddSSOEditor from '@ion/applications/src/pages/admin/accounts/AddSSOEditor';
 *
 *  <AddSSOEditor refreshInitialization={refreshInitialization} />
 *
 * ```
 */
export const AddSSOEditor = ({refreshInitialization}: AddSSOEditorProps) => {
  const dispatch                        = useDispatch();
  const selectedAccount                 = useSelector(addSSOSelectedSelector);
  const [ssoConfigObj, setSSOConfigObj] = useState(null);

  const {
    data,
    save,
    isSuccess,
    isError,
    isLoading
  } = useAddSSOConfiguration();

  useEffect(() => {
    if (isSuccess && data && ssoConfigObj) {
      const accObj = {
        ...selectedAccount,
        ssoMode   : ssoConfigObj?.sso_mode,
        ssoConfig : data?.self?.href
      };
      saveSSOAccount(dispatch, {
        account   : accObj,
        refreshInitialization,
        onUpdated : function() {
          dispatch(addSSOConfig(null, false));
        }
      });
    }
    if (isError) {
      dispatch(toggleUserPrompt(errorAddOptions(Entity.ssoConfiguration)));
    }
  }, [data, isSuccess, isError, ssoConfigObj]);

  const onSave = useCallback(function(ssoConfigData: SSOConfiguration): void {
    setSSOConfigObj(ssoConfigData);
    save({...ssoConfigData});
  }, []);

  const onClose = useCallback(function(): void {
    setSSOConfigObj(null);
    dispatch(addSSOConfig(null, false));
  }, []);

  return (
    <SSOEditor onSave={onSave}
      onClose={onClose}
      refreshInitialization={refreshInitialization}
      selectedAccount={selectedAccount}
      isLoading={isLoading} />
  );
};

export default memo(AddSSOEditor);
